import { MarkdownEditor } from '@/components/MarkdownEditor'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useIntent, useUpdateIntent } from '@/hooks'
import type { IntentDetail, UpdateIntentRequest } from '@/types'
import { Check, Loader2 } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

const NAME_MAX = 256
const EPIC_MAX = 256
const FEATURE_MAX = 256
const DESCRIPTION_MAX = 50000

interface EditIntentDialogProps {
  projectId: string
  intentId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditIntentDialog({ projectId, intentId, open, onOpenChange }: EditIntentDialogProps) {
  const { data: intent, isLoading } = useIntent(projectId, open ? intentId : null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">
            Edit Intent{intent?.number != null ? ` #${String(intent.number).padStart(3, '0')}` : ''}
          </DialogTitle>
        </DialogHeader>

        {(isLoading || !intent) ? (
          <div className="flex items-center justify-center flex-1" role="status" aria-live="polite">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <EditIntentForm
            projectId={projectId}
            intent={intent}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

interface EditIntentFormProps {
  projectId: string
  intent: IntentDetail
  onOpenChange: (open: boolean) => void
}

function EditIntentForm({ projectId, intent, onOpenChange }: EditIntentFormProps) {
  const updateIntent = useUpdateIntent(projectId)

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<UpdateIntentRequest>({
    defaultValues: {
      name: intent.name,
      epic: intent.epic ?? '',
      feature: intent.feature ?? '',
      description: intent.description ?? '',
    },
  })

  const nameValue = watch('name') ?? ''
  const epicValue = watch('epic') ?? ''
  const featureValue = watch('feature') ?? ''
  const descriptionValue = watch('description') ?? ''

  const onSubmit = async (data: UpdateIntentRequest) => {
    try {
      await updateIntent.mutateAsync({
        id: intent.id,
        data: {
          name: data.name.trim(),
          epic: data.epic?.trim() || null,
          feature: data.feature?.trim() || '',
          description: data.description?.trim() || null,
        },
      })
      toast.success('Intent updated successfully')
      onOpenChange(false)
    } catch {
      toast.error('Failed to update intent')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 bg-input-background">
        <div className="max-w-4xl mx-auto py-8 px-6 space-y-4 flex flex-col flex-1 min-h-0 w-full">
          <div className="space-y-2 shrink-0">
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-intent-name">Name <span className="text-red-500">*</span></Label>
              <span className="text-xs text-muted-foreground">{nameValue.trim().length}/{NAME_MAX}</span>
            </div>
            <Input
              id="edit-intent-name"
              data-testid="edit-intent-name-input"
              placeholder="e.g. User authentication improvements"
              {...register('name', {
                required: 'Name is required',
                validate: (value) => {
                  const trimmed = value.trim()
                  if (trimmed.length === 0) return 'Name is required'
                  if (trimmed.length > NAME_MAX) return `Name must be at most ${NAME_MAX} characters`
                  return true
                },
              })}
            />
            {errors.name && (
              <p className="text-sm text-destructive" role="alert">{errors.name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 shrink-0">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-intent-epic">Epic</Label>
                <span className="text-xs text-muted-foreground">{epicValue.trim().length}/{EPIC_MAX}</span>
              </div>
              <Input
                id="edit-intent-epic"
                data-testid="edit-intent-epic-input"
                placeholder="e.g. Identity"
                {...register('epic', {
                  validate: (value) => {
                    const trimmed = (value ?? '').trim()
                    if (trimmed.length > EPIC_MAX) return `Epic must be at most ${EPIC_MAX} characters`
                    return true
                  },
                })}
              />
              {errors.epic && (
                <p className="text-sm text-destructive" role="alert">{errors.epic.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-intent-feature">Feature <span className="text-red-500">*</span></Label>
                <span className="text-xs text-muted-foreground">{featureValue.trim().length}/{FEATURE_MAX}</span>
              </div>
              <Input
                id="edit-intent-feature"
                data-testid="edit-intent-feature-input"
                placeholder="e.g. Logistics"
                {...register('feature', {
                  required: 'Feature is required',
                  validate: (value) => {
                    const trimmed = (value ?? '').trim()
                    if (trimmed.length === 0) return 'Feature is required'
                    if (trimmed.length > FEATURE_MAX) return `Feature must be at most ${FEATURE_MAX} characters`
                    return true
                  },
                })}
              />
              {errors.feature && (
                <p className="text-sm text-destructive" role="alert">{errors.feature.message}</p>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0 space-y-2">
            <div className="flex items-center justify-between shrink-0">
              <Label htmlFor="edit-intent-description">Description</Label>
              <span className="text-xs text-muted-foreground">{descriptionValue.trim().length}/{DESCRIPTION_MAX}</span>
            </div>
            <Controller
              name="description"
              control={control}
              rules={{
                validate: (value) => {
                  if (value && value.trim().length > DESCRIPTION_MAX)
                    return `Description must be at most ${DESCRIPTION_MAX} characters`
                  return true
                },
              }}
              render={({ field }) => (
                <MarkdownEditor
                  id="edit-intent-description"
                  dataTestId="edit-intent-description-input"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="e.g. Describe what this intent covers..."
                  fillHeight
                />
              )}
            />
            {errors.description && (
              <p className="text-sm text-destructive" role="alert">{errors.description.message}</p>
            )}
          </div>
        </div>
      </div>
      <DialogFooter className="px-8 py-4 border-t shrink-0 bg-background">
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={updateIntent.isPending}
        >
          <Check className="h-5 w-5" />
          {updateIntent.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </form>
  )
}
